
import { useState, useCallback } from "react";
import { UserDialog } from "./UserDialog";
import { useOptimizedUsersCache } from "./useOptimizedUsersCache";
import { UserProfile, AccessRequest } from "@/types";
import { useToast } from "@/hooks/use-toast";
import RouteErrorBoundary from "@/components/error/RouteErrorBoundary";
import { UnifiedTableLayout } from "@/components/tables/unified";
import { getUsersTableConfig } from "./usersTableConfig";

export default function UsersAdmin() {
  const { data, isLoading, refreshData } = useOptimizedUsersCache();
  const [selectedUser, setSelectedUser] = useState<UserProfile | AccessRequest | null>(null);
  const [isAccessRequest, setIsAccessRequest] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();

  // Fetch function that returns cached data directly
  const fetchUsersData = useCallback(async () => {
    return data;
  }, [data]);

  const handleUserClick = (user: UserProfile | AccessRequest, isRequest = false) => {
    setSelectedUser(user);
    setIsAccessRequest(isRequest);
    setIsNewUser(false);
    setIsDialogOpen(true);
  };

  const handleOpenNewUserDialog = () => {
    // Create empty user profile for the dialog
    const emptyUser = {
      id: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: 'customer',
      isApproved: true,
      createdAt: new Date().toISOString()
    } as UserProfile;
    
    setSelectedUser(emptyUser);
    setIsAccessRequest(false);
    setIsNewUser(true);
    setIsDialogOpen(true);
  };

  const handleCSVImport = async (rows: any[]): Promise<void> => {
    try {
      toast({ 
        title: "Processing CSV import", 
        description: `Processing ${rows.length} rows...` 
      });
      
      // Mock import for now
      // This would need proper integration with your backend
      toast({ title: "CSV imported successfully", description: `Added ${rows.length} users` });
      // Note: fetchUsersData will be called automatically by OptimizedReusableTablePage after import
    } catch (err: any) {
      toast({ 
        title: "Import failed", 
        description: err.message || "Unknown error occurred", 
        variant: "destructive" 
      });
      throw err;
    }
  };

  const handleExport = async () => {
    toast({ title: "CSV export coming soon!" });
  };

  const handleExportTemplate = async () => {
    toast({ title: "Template export coming soon!" });
  };

  const tableConfig = getUsersTableConfig(handleUserClick, handleOpenNewUserDialog);

  return (
    <RouteErrorBoundary>
      <UnifiedTableLayout
        title={tableConfig.pageTitle}
        data={data}
        columns={tableConfig.columns}
        searchFields={tableConfig.searchFields}
        isLoading={isLoading}
        onAddNew={handleOpenNewUserDialog}
        onImport={() => handleCSVImport([])}
        onExport={handleExport}
        onExportTemplate={handleExportTemplate}
        onRefresh={refreshData}
        rowActions={tableConfig.rowActions}
        onRowClick={tableConfig.onRowClick}
        addButtonText={tableConfig.addButtonText}
      />
      
      {selectedUser && (
        <UserDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          user={selectedUser}
          isAccessRequest={isAccessRequest}
          isNewUser={isNewUser}
        />
      )}
    </RouteErrorBoundary>
  );
}
